/* eslint-disable react/display-name */
import * as React from 'react'
import * as z from 'zod'

import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  getMiddleCenterBias,
  roundedRect,
  ProvideEditorComponent,
} from '@glideapps/glide-data-grid'
import {
  getSquareBB,
  getSquareXPosFromAlign,
  isOverEditableRegion,
  pointIsWithinBB,
} from './utils'
import { blockType } from '@basehub/data/replicache/index'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { CELL_BUTTON_SIZE, SPRITE_MARGIN, SPRITE_SIZE } from '../theme'

export const blockCellSchema = z.object({
  kind: z.literal('block-cell'),
  title: z.string(),
  type: blockType.optional(),
  highlight: z.boolean().optional(),
  hidden: z.boolean().optional(),
  placeholder: z.string().optional(),
  onClick: z.function(z.tuple([z.object({ metaKey: z.boolean() })])),
})

export type BlockCellProps = z.infer<typeof blockCellSchema> & {
  readonly event?: React.ChangeEvent<HTMLInputElement>
}

export type BlockCell = CustomCell<BlockCellProps>

export const BlockCellRenderer: CustomRenderer<BlockCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is BlockCell =>
    (cell.data as any).kind === 'block-cell',
  needsHover: true,
  needsHoverPosition: true,
  draw: (args) => {
    const {
      ctx,
      cell,
      rect,
      hoverX,
      hoverY,
      theme,
      spriteManager,
      highlighted,
    } = args

    const centerY = rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)

    //  draw block type icon
    if (cell.data.type) {
      spriteManager.drawSprite(
        cell.data.type,
        cell.data.highlight ? 'special' : 'normal',
        ctx,
        rect.x + theme.cellHorizontalPadding,
        centerY - SPRITE_SIZE / 2,
        SPRITE_SIZE,
        {
          ...args.theme,
          accentColor: args.theme.accentFg,
          bgIconHeader: args.theme.textDark,
        }
      )
    }

    const xPad = theme.cellHorizontalPadding
    const drawX = rect.x + xPad
    const yPad = theme.cellVerticalPadding
    ctx.save()

    ctx.font = theme.baseFontFull
    const isPlaceholder = cell.data.title === '' && cell.data.placeholder
    let fillStyle = theme.textDark
    if (cell.data.highlight) fillStyle = theme.accentFg
    else if (isPlaceholder && !cell.data.hidden) {
      fillStyle = fillStyle + 66 // 40% opacity for placeholder only if not hidden, if hidden, the opacity is given to the hole row
    }
    ctx.fillStyle = fillStyle
    const textDrawX = cell.data.type
      ? drawX + SPRITE_SIZE + SPRITE_MARGIN
      : drawX
    ctx.fillText(
      (cell.data.title || cell.data?.placeholder) ?? '',
      textDrawX,
      centerY
    )

    ctx.restore()

    const bb = getSquareBB(
      rect.x,
      Math.floor(rect.y + rect.height / 2),
      rect.width * 2
    )
    const hovered =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bb)
        : false

    if (!hovered && !highlighted) return true

    // draw button
    ctx.beginPath()

    const posX = getSquareXPosFromAlign(
      'right',
      rect.x,
      rect.width,
      yPad,
      CELL_BUTTON_SIZE
    )
    const bbAction = getSquareBB(posX, centerY, CELL_BUTTON_SIZE)
    const hasHoveredLink =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bbAction)
        : false
    roundedRect(
      ctx,
      posX - CELL_BUTTON_SIZE / 2,
      centerY - CELL_BUTTON_SIZE / 2,
      CELL_BUTTON_SIZE,
      CELL_BUTTON_SIZE,
      6
    )
    ctx.fillStyle = hasHoveredLink ? theme.bgHeaderHovered : theme.bgHeader
    ctx.fill()

    // draw button stroke
    ctx.lineWidth = 1.2
    ctx.strokeStyle = hovered
      ? theme.bgBubble
      : theme.headerBottomBorderColor ?? '#252525'
    ctx.stroke()

    // draw button arrow
    spriteManager.drawSprite(
      'arrow-top-right',
      'normal',
      ctx,
      posX - SPRITE_SIZE / 2,
      centerY - SPRITE_SIZE / 2,
      SPRITE_SIZE,
      theme
    )

    return true
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onSelect: (e) => {
    if (isOverEditableRegion(e)) {
      e.preventDefault()
    }
  },
  onClick: (e) => {
    const clickedAction = isOverEditableRegion(e)
    if (!clickedAction) {
      return undefined
    }

    // const isPressingMeta = e.event.metaKey || e.event.ctrlKey
    e.preventDefault()
    e.cell.data.onClick({ metaKey: e.metaKey })
  },
  onPaste: (toPaste, cell) => {
    return { ...cell, title: toPaste }
  },
}

const Editor: ProvideEditorComponent<BlockCell> = (cell) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const cellValue =
    cell.value.data.title === 'Untitled' ? '' : cell.value.data.title
  const inputValue = cell.initialValue ?? cellValue
  const inputWasInitializedRef = React.useRef(false)

  React.useLayoutEffect(() => {
    if (inputWasInitializedRef.current) return

    if (inputValue === cellValue) {
      if (inputWasInitializedRef.current) return
      inputWasInitializedRef.current = true
      inputRef.current?.select()
    } else {
      cell.onChange({
        ...cell.value,
        data: { ...cell.value.data, title: inputValue },
      })
      inputWasInitializedRef.current = true
    }
  }, [cell, cellValue, inputValue])

  return (
    <div className="relative flex-1 flex flex-col">
      {cell.value.data.type && (
        <BlockIcon
          block={{ type: cell.value.data.type }}
          iconProps={{
            className: clsx(
              'size-[14px] select-none pointer-events-none absolute left-3 top-[13px]',
              cell.value.data.highlight
                ? 'text-dark-control-highlight'
                : 'text-dark-muted'
            ),
          }}
        />
      )}
      <input
        ref={inputRef}
        style={{ fontSize: 'var(--gdg-editor-font-size)' }}
        className={clsx(
          'border-none outline-none bg-transparent flex-1 placeholder:text-dark-muted/40',
          cell.value.data.type && 'pl-[34px]',
          cell.value.data.highlight
            ? 'text-dark-control-highlight'
            : 'text-dark-muted'
        )}
        autoFocus
        placeholder={cell.value.data.placeholder}
        defaultValue={inputValue}
        onChange={(event) => {
          cell.onChange({
            ...cell.value,
            data: { ...cell.value.data, title: event.target.value },
          })
        }}
      />
    </div>
  )
}
